// src/components/select/MediumSelector.tsx

import { DangerousTwoTone } from '@mui/icons-material'
import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  SxProps,
  TextField,
  Theme,
} from '@mui/material'
import { ChangeEvent, useEffect, useState } from 'react'

import { FolderId, SensorId, SensorRecord } from '@/types/domain'

import { HStack } from '..'
import { sensorType, SensorIcon, getStatusColor } from '../../api'
import { useFolderSensorIds, useFolderSensors } from '../../hooks'

// filter

const ENERGY_SENSORS = f =>
  f.sensor_target === 'performance' &&
  (f.sub_sensor_target === 'energy' || f.sub_sensor_target === 'energy_detail')

const WATER_SENSORS = f =>
  f.sensor_target === 'performance' &&
  (f.sub_sensor_target === 'water' || f.sub_sensor_target === 'water_detail')

const GAS_SENSORS = f =>
  f.sensor_target === 'performance' &&
  (f.sub_sensor_target === 'gas' || f.sub_sensor_target === 'gas_detail')

// component

type Props = {
  consumptionData: any[]
  id: FolderId
  label?: string
  size?: number
  onChange?: (_: ChangeEvent<HTMLInputElement>) => void
  sx?: SxProps<Theme>
  value: string
}

export const MediumSelector = ({
  consumptionData,
  id,
  label = 'Energiemedium',
  size = 25,
  onChange,
  value,
  sx,
  ...rest
}: Props) => {
  const fSensors: SensorRecord[] = useFolderSensors(id)
  const fsIds: SensorId[] = useFolderSensorIds(id)

  const [mediumTypes, setMediumTypes] = useState([])
  const [groupStatus, setGroupStatus] = useState([])

  // exceptions
  const noMedia = mediumTypes.length === 0

  useEffect(() => {
    const mediumTypes = [
      ...new Set(
        consumptionData
          // remove consumptionData not assigned to operator sensors
          .filter(f => fsIds.includes(f.entity_id))

          // get sensor types in consumptionData for available medium types
          .map(s => s.type_in_folder)

          // merge main & sub sensor types (there is no distinction in UI)
          .map(s => s.replace('_detail', ''))
      ),
    ]

    setMediumTypes(mediumTypes)
  }, [consumptionData, fsIds])

  useEffect(() => {
    if (fSensors.length === 0 || fsIds.length === 0) return

    setGroupStatus(fSensors.filter(f => fsIds.includes(f.entity_id)))
  }, [fSensors, fsIds])

  return mediumTypes.length === 0 ? null : (
    <TextField
      label={label}
      select
      variant={'standard'}
      onChange={onChange}
      sx={sx}
      value={value}
      {...rest}
    >
      {noMedia && (
        <MenuItem disabled>
          <HStack>
            <ListItemIcon>
              <DangerousTwoTone color={'error'} />
            </ListItemIcon>

            <ListItemText>Kein Medium</ListItemText>
          </HStack>
        </MenuItem>
      )}

      {mediumTypes.map(medium => {
        let status

        switch (medium) {
          case 'energy':
            status = groupStatus
              .filter(ENERGY_SENSORS)
              .map(s => ({ ...s, ...s.attributes }))
            break

          case 'water':
            status = groupStatus
              .filter(WATER_SENSORS)
              .map(s => ({ ...s, ...s.attributes }))
            break

          case 'gas':
            status = groupStatus
              .filter(GAS_SENSORS)
              .map(s => ({ ...s, ...s.attributes }))
            break

          default:
            console.error('unhandled medium:', medium)
            break
        }

        const color = getStatusColor(status)

        return (
          <MenuItem key={medium} value={medium}>
            <HStack>
              <ListItemIcon>
                <Box
                  component={'img'}
                  src={SensorIcon(medium, color)}
                  sx={{ width: size }}
                />
              </ListItemIcon>

              <ListItemText>{sensorType(medium)}</ListItemText>
            </HStack>
          </MenuItem>
        )
      })}
    </TextField>
  )
}
